import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { motion } from 'framer-motion';
import { RootState } from '../app/store';
import { fetchReports, generateReport, previewReport } from '../app/slices/reportSlice';
import { addToast } from '../app/slices/uiSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import QuickActionCard from '../components/QuickActionCard';
import {
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { taxUtils } from '../lib/taxEngine';
import { track } from '../lib/firebase';
import { trackUsage, startDwellTimer } from '../lib/telemetry';
import InlinePanel from '../ui/common/InlinePanel';
import { getFlagFromSearch } from '../lib/urlState';
import { Timestamp } from 'firebase/firestore';
import { Report } from '../types/models';

const Reports: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { reports, currentReport, isLoading, isGenerating } = useSelector(
    (state: RootState) => state.reports
  );

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showIncomeTaxModal, setShowIncomeTaxModal] = useState(false);
  const generateBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const incomeTaxBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const reportPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const [showExactValues, setShowExactValues] = useState(false);
  const [reportPeriod, setReportPeriod] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Jan 1
    end: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0], // Dec 31
  });

  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchReports(user.uid));
    }
  }, [dispatch, user?.uid]);

  // URL-driven state (?report=1)
  const reportOpenFromUrl = useMemo(
    () => getFlagFromSearch(new URLSearchParams(window.location.search), 'report'),
    []
  );
  useEffect(() => {
    if (reportOpenFromUrl) setShowGenerateModal(true);
  }, [reportOpenFromUrl]);

  useEffect(() => {
    // Track page view and dwell time
    trackUsage('usage_page_view', { page: 'Reports' }).catch(console.warn);
    const stop = startDwellTimer('Reports');
    return stop;
  }, []);
  const setReportFlag = (value: boolean) => {
    const params = new URLSearchParams(window.location.search);
    if (value) params.set('report', '1');
    else params.delete('report');
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState(null, '', newUrl);
  };

  const handleGenerateReport = async () => {
    if (!user?.uid) return;

    try {
      // Create end date at end of day to include the full selected date
      const endDate = new Date(reportPeriod.end);
      endDate.setHours(23, 59, 59, 999);

      await dispatch(
        generateReport({
          userId: user.uid,
          period: {
            start: new Date(reportPeriod.start),
            end: endDate,
          },
          provinceCode: user.province,
        })
      );

      dispatch(
        addToast({
          message: 'Report generated successfully! 📊',
          type: 'success',
        })
      );

      setShowGenerateModal(false);
    } catch {
      dispatch(
        addToast({
          message: 'Failed to generate report',
          type: 'error',
        })
      );
    }
  };

  const handlePreviewReport = async () => {
    if (!user?.uid) return;

    try {
      // Create end date at end of day to include the full selected date
      const endDate = new Date(reportPeriod.end);
      endDate.setHours(23, 59, 59, 999);

      const result = await dispatch(
        previewReport({
          userId: user.uid,
          period: {
            start: new Date(reportPeriod.start),
            end: endDate,
          },
          provinceCode: user.province,
        })
      ).unwrap();

      // Generate PDF preview
      const { ReportGenerator } = await import('../lib/reportGenerator');
      const generator = new ReportGenerator();
      const pdfBuffer = generator.generateCRAReport(
        {
          ...result,
          id: 'preview',
          createdAt: Timestamp.now(),
        } as Report,
        user,
        result.sessions,
        result.expenses
      );

      // Create blob and open in new tab
      const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      dispatch(
        addToast({
          message: 'Report preview opened in new tab',
          type: 'success',
        })
      );
    } catch {
      dispatch(
        addToast({
          message: 'Failed to generate report preview',
          type: 'error',
        })
      );
    }
  };

  const handleGenerateIncomeTaxSummary = async () => {
    if (!user?.uid) return;

    try {
      // Create end date at end of day to include the full selected date
      const endDate = new Date(reportPeriod.end);
      endDate.setHours(23, 59, 59, 999);

      const result = await dispatch(
        previewReport({
          userId: user.uid,
          period: {
            start: new Date(reportPeriod.start),
            end: endDate,
          },
          provinceCode: user.province,
        })
      ).unwrap();

      // Generate income tax focused PDF
      const { ReportGenerator } = await import('../lib/reportGenerator');
      const generator = new ReportGenerator();
      const pdfBuffer = generator.generateIncomeTaxSummary(
        {
          ...result,
          id: 'income-tax-summary',
          createdAt: Timestamp.now(),
        } as Report,
        user,
        result.sessions,
        result.expenses
      );

      // Create blob and open in new tab
      const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      dispatch(
        addToast({
          message: 'Income Tax Summary opened in new tab',
          type: 'success',
        })
      );

      setShowIncomeTaxModal(false);
    } catch {
      dispatch(
        addToast({
          message: 'Failed to generate income tax summary',
          type: 'error',
        })
      );
    }
  };

  const handleDownloadReport = (_reportId: string) => {
    // This would implement PDF download functionality
    setShowGenerateModal(true);
  };

  const formatPeriod = (start: Date, end: Date) => {
    return `${start.toLocaleDateString('en-CA')} - ${end.toLocaleDateString('en-CA')}`;
  };

  const formatCurrency = (amount: number) => {
    if (showExactValues) {
      return taxUtils.formatCurrency(amount);
    } else {
      return taxUtils.formatCurrency(taxUtils.roundToDollar(amount));
    }
  };

  const formatCRALine = (amount: number, isLine115 = false) => {
    if (isLine115 || !showExactValues) {
      // Line 115 is always rounded, or when showing CRA rounded view
      return taxUtils.formatCurrency(taxUtils.roundToDollar(amount));
    } else {
      // Show exact values
      return taxUtils.formatCurrency(amount);
    }
  };

  // Show loading skeleton only for initial load
  if (isLoading && reports.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>

        {/* Quick actions skeleton */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="card text-center p-6">
                <div className="h-12 w-12 bg-gray-200 rounded-full mx-auto mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full mx-auto mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-20 mx-auto"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Report table skeleton */}
        <div className="animate-pulse">
          <div className="card">
            <div className="card-header">
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            </div>
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex space-x-4 p-4">
                  <div className="h-4 bg-gray-200 rounded flex-1"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Branding Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-md"
      >
        <div className="flex items-center justify-center space-x-3 mb-2">
          <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
            AutoTaxPilot™
          </h1>
        </div>
        <p className="text-center text-sm text-gray-600 font-medium">
          Built for Uber Drivers • Reliable & Accurate • CRA-Compliant
        </p>
      </motion.div>

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-3xl font-bold leading-7 text-gray-900 sm:text-4xl sm:truncate"
          >
            CRA Reports
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-2 text-base text-gray-600"
          >
            Generate GST/HST returns and income tax reports
          </motion.p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            ref={generateBtnRef}
            onClick={() => {
              setShowGenerateModal(true);
              setReportFlag(true);
              track('panel_opened', { panel: 'report_new' });
            }}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
      >
        <QuickActionCard
          icon={DocumentTextIcon}
          title="GST/HST Return"
          subtitle="Generate quarterly or annual GST/HST filing"
          ctaLabel="Generate"
          onClick={() => {
            trackUsage('usage_action', { page: 'Reports', action: 'generate_gst_hst' }).catch(
              console.warn
            );
            setShowGenerateModal(true);
            setReportFlag(true);
          }}
          tone="primary"
        />

        <QuickActionCard
          icon={CalendarIcon}
          title="Income Tax Summary"
          subtitle="Annual income and expense summary"
          ctaLabel="Generate"
          onClick={() => {
            setShowIncomeTaxModal(true);
            track('panel_opened', { panel: 'income_tax_generate' });
          }}
          tone="success"
        />

        <QuickActionCard
          icon={CogIcon}
          title="T2125 Statement"
          subtitle="Generate T2125 business expense summary useful for TurboTax-Friendly as an T1 T2125 filing"
          ctaLabel="Generate T2125"
          onClick={() => {
            trackUsage('usage_action', { page: 'Reports', action: 'generate_t2125' }).catch(
              console.warn
            );
            setShowIncomeTaxModal(true);
            track('panel_opened', { panel: 't2125_generate' });
          }}
          tone="purple"
        />
      </motion.div>

      {/* Tax Settings Link */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Tax Settings</h3>
        </div>
        <div className="text-center py-6">
          <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h4 className="mt-2 text-sm font-medium text-gray-900">Configure Tax Preferences</h4>
          <p className="mt-1 text-sm text-gray-500">
            Set your filing frequency, preferred tax periods, and other tax-related preferences.
          </p>
          <a
            href="/profile"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Go to Profile Settings
            <ArrowRightOnRectangleIcon className="ml-2 -mr-1 h-4 w-4" />
          </a>
        </div>
      </motion.div>

      {/* Current Report Preview */}
      {currentReport && currentReport.period && currentReport.gstLines && (
        <motion.div
          ref={reportPreviewRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="card-header">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Report Preview</h3>
                <p className="text-sm text-gray-500">
                  {formatPeriod(currentReport.period.start, currentReport.period.end)}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {showExactValues ? 'Exact' : 'CRA Rounded'}
                </span>
                <button
                  onClick={() => setShowExactValues(!showExactValues)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    showExactValues ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showExactValues ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* CRA Lines Summary */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">GST/HST Return Lines</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Line 90 - Total taxable sales:</span>
                  <span className="text-sm font-medium">
                    {formatCRALine(currentReport.gstLines.line90)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Line 91 - Exempt supplies:</span>
                  <span className="text-sm font-medium">
                    {formatCRALine(currentReport.gstLines.line91)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Line 101 - Total sales and other revenue:
                  </span>
                  <span className="text-sm font-medium">
                    {formatCRALine(currentReport.gstLines.line101)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Line 103 - GST collected:</span>
                  <span className="text-sm font-medium">
                    {formatCRALine(currentReport.gstLines.line103)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Line 106 - Input Tax Credits:</span>
                  <span className="text-sm font-medium">
                    {formatCRALine(currentReport.gstLines.line106)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Line 108 - Adjustments:</span>
                  <span className="text-sm font-medium">
                    {formatCRALine(currentReport.gstLines.line108)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium text-gray-900">Line 109 - Net Tax:</span>
                  <span
                    className={`text-sm font-bold ${currentReport.gstLines.line109 >= 0 ? 'text-warning-600' : 'text-success-600'}`}
                  >
                    {formatCRALine(currentReport.gstLines.line109)}
                  </span>
                </div>
                {showExactValues && (
                  <div className="flex justify-between border-t pt-2 bg-blue-50 -mx-4 px-4 py-2 rounded">
                    <span className="text-sm font-medium text-blue-900">
                      Line 115 - Amount owing/refund:
                    </span>
                    <span className="text-sm font-bold text-blue-900">
                      {formatCRALine(
                        currentReport.gstLines.line115 || currentReport.gstLines.line109,
                        true
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">Income Summary</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Income:</span>
                  <span className="text-sm font-medium">
                    {formatCurrency(currentReport.totalIncome || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Expenses:</span>
                  <span className="text-sm font-medium">
                    {formatCurrency(currentReport.totalExpenses || 0)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium text-gray-900">Net Income:</span>
                  <span className="text-sm font-bold text-gray-900">
                    {formatCurrency(currentReport.netIncome || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {showExactValues && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Exact View:</strong> Shows precise calculations. Line 115 is always rounded
                to the nearest dollar as required by CRA.
              </p>
            </div>
          )}

          {!showExactValues && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>CRA Rounded View:</strong> Shows amounts rounded to the nearest dollar as
                they would appear on your CRA filing.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button onClick={() => handleDownloadReport('preview')} className="btn-secondary">
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download PDF
            </button>
            <button onClick={handleGenerateReport} className="btn-primary" disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Generating...
                </>
              ) : (
                'Save Report'
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* Reports History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Generated Reports</h3>
        </div>

        {reports.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No reports yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Generate your first CRA report to get started.
            </p>
            <div className="mt-6">
              <button onClick={() => setShowGenerateModal(true)} className="btn-primary">
                <PlusIcon className="h-4 w-4 mr-2" />
                Generate First Report
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net GST
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Income
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Generated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report, index) => (
                  <motion.tr
                    key={report.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatPeriod(report.period.start, report.period.end)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${report.netGST >= 0 ? 'text-warning-600' : 'text-success-600'}`}
                      >
                        {taxUtils.formatCurrency(report.netGST)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {taxUtils.formatCurrency(report.netIncome)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(report.createdAt.toDate()).toLocaleDateString('en-CA')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            // Set the current report for viewing
                            dispatch({ type: 'reports/setCurrentReport', payload: report });
                            track('report_viewed', { reportId: report.id });

                            // Scroll to Report Preview section after a short delay to ensure it's rendered
                            setTimeout(() => {
                              if (reportPreviewRef.current) {
                                reportPreviewRef.current.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start',
                                });
                              }
                            }, 100);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                          title="View Report"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadReport(report.id)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Download PDF"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Generate Report Inline Panel */}
      <InlinePanel
        open={showGenerateModal}
        title="Generate CRA Report"
        subtitle="Select the reporting period for your GST/HST return and income summary."
        onClose={() => {
          setShowGenerateModal(false);
          setReportFlag(false);
          track('panel_closed', { panel: 'report_new' });
        }}
        className="max-w-2xl mx-auto"
        returnFocusRef={generateBtnRef as unknown as React.RefObject<HTMLElement>}
        regionId="report-generate-panel-title"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={reportPeriod.start}
              onChange={e => setReportPeriod(prev => ({ ...prev, start: e.target.value }))}
              className="input"
            />
          </div>
          <div>
            <label className="label">End Date</label>
            <input
              type="date"
              value={reportPeriod.end}
              onChange={e => setReportPeriod(prev => ({ ...prev, end: e.target.value }))}
              className="input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={handlePreviewReport}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Preview'}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                track('report_generated', { start: reportPeriod.start, end: reportPeriod.end });
                handleGenerateReport();
              }}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate'}
            </button>
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="text-xs text-gray-600">
              Tax settings can be configured in your Profile
            </div>
            <a href="/profile" className="btn-secondary btn-sm inline-flex items-center">
              Go to Profile
              <ArrowRightOnRectangleIcon className="ml-1 h-3 w-3" />
            </a>
          </div>
        </div>
      </InlinePanel>

      {/* Generate Income Tax Summary Inline Panel */}
      <InlinePanel
        open={showIncomeTaxModal}
        title="Generate Income Tax Summary"
        subtitle="Generate a summary focused on income tax deductions and business expenses."
        onClose={() => {
          setShowIncomeTaxModal(false);
          track('panel_closed', { panel: 'income_tax_generate' });
        }}
        className="max-w-2xl mx-auto"
        returnFocusRef={incomeTaxBtnRef as unknown as React.RefObject<HTMLElement>}
        regionId="income-tax-generate-panel-title"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={reportPeriod.start}
              onChange={e => setReportPeriod(prev => ({ ...prev, start: e.target.value }))}
              className="input"
            />
          </div>
          <div>
            <label className="label">End Date</label>
            <input
              type="date"
              value={reportPeriod.end}
              onChange={e => setReportPeriod(prev => ({ ...prev, end: e.target.value }))}
              className="input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowIncomeTaxModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-success"
              onClick={() => {
                track('income_tax_summary_generated', {
                  start: reportPeriod.start,
                  end: reportPeriod.end,
                });
                handleGenerateIncomeTaxSummary();
              }}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Summary'}
            </button>
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="text-xs text-gray-600">
              Tax settings can be configured in your Profile
            </div>
            <a href="/profile" className="btn-secondary btn-sm inline-flex items-center">
              Go to Profile
              <ArrowRightOnRectangleIcon className="ml-1 h-3 w-3" />
            </a>
          </div>
        </div>
      </InlinePanel>
    </div>
  );
};

export default Reports;
